/**
 * Sitzungs- und Oberflächenzustand.
 *
 * Getrennt gehalten wie im Handoff beschrieben:
 *  - Server-Zustand (Angebote, Kunden, Einstellungen) liegt in React Query.
 *  - Oberflächenzustand (offene Modals, Toast, Filter) liegt hier.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from './api';
import { darstellungAnwenden } from './hilfen/darstellung';
import type { Meta, Nutzer } from './typen';

/** Welches Modal ist gerade offen? */
export type ModalArt =
  | null
  | { art: 'einstellungen' }
  | { art: 'adminCenter' }
  | { art: 'profil' }
  | { art: 'farben' }
  | { art: 'changelog'; alle: boolean }
  | { art: 'neuerKunde'; kundeId?: number; herkunft: 'kunden' | 'angebot' };

interface KoraZustand {
  nutzer: Nutzer | null;
  meta: Meta | null;
  ladend: boolean;
  angemeldet: boolean;

  /** Bereichszugriff des angemeldeten Nutzers. */
  darf: (bereich: string) => boolean;

  toast: string;
  toastZeigen: (text: string) => void;
  toastSchliessen: () => void;

  modal: ModalArt;
  modalOeffnen: (m: ModalArt) => void;
  modalSchliessen: () => void;

  /** Mobiles Menü. */
  menuOffen: boolean;
  menuUmschalten: () => void;
  menuSchliessen: () => void;

  neuLaden: () => void;
  abmelden: () => Promise<void>;
  nachAnmeldung: (n: Nutzer) => void;
}

const Kontext = createContext<KoraZustand | null>(null);

export function KoraProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState('');
  const [modal, setModal] = useState<ModalArt>(null);
  const [menuOffen, setMenuOffen] = useState(false);
  const toastTimer = useRef<number | undefined>(undefined);

  const nutzerAbfrage = useQuery({
    queryKey: ['ich'],
    queryFn: async () => {
      try {
        return await api.hole<Nutzer>('/auth/ich');
      } catch (fehler) {
        if (fehler instanceof ApiFehler && fehler.status === 401) return null;
        throw fehler;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const nutzer = nutzerAbfrage.data ?? null;

  const metaAbfrage = useQuery({
    queryKey: ['meta'],
    queryFn: () => api.hole<Meta>('/meta'),
    enabled: !!nutzer,
    staleTime: 5 * 60_000,
  });

  const toastZeigen = useCallback((text: string) => {
    if (!text) return;
    setToast(text);
    window.clearTimeout(toastTimer.current);
    // 5,2 Sekunden – wie im Design festgelegt.
    toastTimer.current = window.setTimeout(() => setToast(''), 5200);
  }, []);

  const toastSchliessen = useCallback(() => {
    window.clearTimeout(toastTimer.current);
    setToast('');
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  // Darstellung: Sobald der Nutzer da ist, gewinnt sein gespeicherter Wert
  // gegen die Kopie, die das Skript in index.html vorab gesetzt hat.
  //
  // Bei „system" hängen wir zusätzlich am Betriebssystem: Windows schaltet
  // abends von selbst um, und Kora soll das mitmachen, ohne dass jemand die
  // Seite neu lädt.
  useEffect(() => {
    const thema = nutzer?.thema ?? 'hell';
    const ampel = nutzer?.ampel ?? 'standard';
    darstellungAnwenden(thema, ampel);
    if (thema !== 'system') return;

    const abfrage = window.matchMedia('(prefers-color-scheme: dark)');
    const beiWechsel = () => darstellungAnwenden(thema, ampel);
    abfrage.addEventListener('change', beiWechsel);

    // Zusätzlich beim Zurückkommen prüfen. Der übliche Fall ist nämlich nicht,
    // dass jemand Kora beim Umschalten anschaut: Windows wechselt am Abend von
    // selbst, während der Rechner gesperrt ist oder ein anderes Fenster vorne
    // liegt. Wer dann zurückkommt, soll das richtige Kora sehen und nicht erst
    // neu laden müssen.
    document.addEventListener('visibilitychange', beiWechsel);
    window.addEventListener('focus', beiWechsel);

    return () => {
      abfrage.removeEventListener('change', beiWechsel);
      document.removeEventListener('visibilitychange', beiWechsel);
      window.removeEventListener('focus', beiWechsel);
    };
  }, [nutzer?.thema, nutzer?.ampel]);

  // Diese beiden brauchen eine stabile Identität: das Grundgerüst schließt das
  // mobile Menü in einem Effekt beim Seitenwechsel. Würde sich die Funktion bei
  // jedem Zustandswechsel neu bilden, liefe der Effekt sofort nach dem Öffnen
  // erneut und schlösse das Menü wieder.
  const menuUmschalten = useCallback(() => setMenuOffen((a) => !a), []);
  const menuSchliessen = useCallback(() => setMenuOffen(false), []);

  const abmelden = useCallback(async () => {
    await api.sende('/auth/abmelden');
    queryClient.clear();
    await queryClient.invalidateQueries();
    window.location.assign('/');
  }, [queryClient]);

  const nachAnmeldung = useCallback(
    (n: Nutzer) => {
      queryClient.setQueryData(['ich'], n);
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const wert = useMemo<KoraZustand>(
    () => ({
      nutzer,
      meta: metaAbfrage.data ?? null,
      ladend: nutzerAbfrage.isLoading || (!!nutzer && metaAbfrage.isLoading),
      angemeldet: !!nutzer,
      darf: (bereich: string) => !!nutzer?.rechte[bereich],
      toast,
      toastZeigen,
      toastSchliessen,
      modal,
      modalOeffnen: (m: ModalArt) => {
        setModal(m);
        setMenuOffen(false);
      },
      modalSchliessen: () => setModal(null),
      menuOffen,
      menuUmschalten,
      menuSchliessen,
      neuLaden: () => void queryClient.invalidateQueries(),
      abmelden,
      nachAnmeldung,
    }),
    [
      nutzer,
      metaAbfrage.data,
      metaAbfrage.isLoading,
      nutzerAbfrage.isLoading,
      toast,
      toastZeigen,
      toastSchliessen,
      modal,
      menuOffen,
      menuUmschalten,
      menuSchliessen,
      queryClient,
      abmelden,
      nachAnmeldung,
    ],
  );

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useKora(): KoraZustand {
  const wert = useContext(Kontext);
  if (!wert) throw new Error('useKora muss innerhalb von KoraProvider verwendet werden.');
  return wert;
}

/** Kurzzugriff auf Meta-Daten, die fast jede Seite braucht. */
export function useMeta(): Meta {
  const { meta } = useKora();
  if (!meta) throw new Error('Meta-Daten sind noch nicht geladen.');
  return meta;
}
